/**
 * LA POPUP « FICHIER VERROUILLÉ » (17/09/2026).
 *
 * Théo : « tu peux lire les docs ; si tu veux modifier, bloque, popup fichier
 * verrouillé, il faut rentrer le code pour déverrouiller. Fondateur ; exploitation et
 * chef d'équipe : renvoie vers moi ou Mathilde. »
 *
 * COMMENT ELLE S'OUVRE — toute seule. `lib/api.js` intercepte les réponses 423 et
 * émet `cadenas:verrouille` avec le corps du refus (voir backend/utils/cadenas.py,
 * `detail_refus`). Aucun écran n'a besoin de connaître ce composant : il suffit qu'il
 * passe par l'instance axios centrale. Un écran qui veut recharger après un
 * déverrouillage écoute `cadenas:deverrouille`.
 *
 * CE QU'ELLE PROPOSE
 *   · fondateur (rôle admin, Théo et Mathilde) : le CODE + un motif, puis le retrait ;
 *   · tous les autres : le nom des personnes à qui s'adresser, et rien d'autre.
 * Le rôle ne suffit jamais : sans le bon code, le serveur refuse (403).
 */
import { useCallback, useEffect, useState } from 'react';
import { Lock, LockOpen, SpinnerGap, Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

export const EVT_VERROUILLE = 'cadenas:verrouille';
export const EVT_DEVERROUILLE = 'cadenas:deverrouille';

/** Appelé par lib/api.js sur toute réponse 423. */
export function signalerVerrouillage(detail) {
  try {
    window.dispatchEvent(new CustomEvent(EVT_VERROUILLE, { detail: detail || {} }));
  } catch { /* navigateur sans CustomEvent : le toast de l'écran suffit */ }
}

const frDate = (iso) => {
  const s = String(iso || '');
  return s.length >= 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '';
};

export default function DialogueCadenasVerrouille() {
  const [cible, setCible] = useState(null);
  const [politique, setPolitique] = useState(null);
  const [code, setCode] = useState('');
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState('');

  const fermer = useCallback(() => {
    setCible(null); setCode(''); setMotif(''); setErreur(''); setPolitique(null);
  }, []);

  useEffect(() => {
    const ouvrir = (e) => {
      setCible((e && e.detail) || {});
      setCode(''); setMotif(''); setErreur(''); setPolitique(null);
    };
    window.addEventListener(EVT_VERROUILLE, ouvrir);
    return () => window.removeEventListener(EVT_VERROUILLE, ouvrir);
  }, []);

  // Qui est devant l'écran : peut-il saisir le code, ou faut-il renvoyer vers quelqu'un ?
  useEffect(() => {
    if (!cible) return;
    let vivant = true;
    api.get('/api/cadenas/politique')
      .then(({ data }) => { if (vivant) setPolitique(data || {}); })
      .catch(() => { if (vivant) setPolitique({ peut_saisir_le_code: false, contacter: ['Théo Mercier', 'Mathilde'] }); });
    return () => { vivant = false; };
  }, [cible]);

  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') fermer(); };
    if (cible) window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [cible, fermer]);

  if (!cible) return null;

  const peutSaisir = !!(politique && politique.peut_saisir_le_code);
  const contacts = (politique && politique.contacter) || ['Théo Mercier', 'Mathilde'];
  const collection = cible.collection || '';
  const id = cible.id || '';
  const deverrouillable = peutSaisir && collection && id;

  const deverrouiller = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setErreur('');
    if (code.trim().length < 6) { setErreur('Le code fait au moins 6 caractères.'); return; }
    if (motif.trim().length < 3) { setErreur('Un motif est obligatoire : il est tracé au journal.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/api/cadenas/${collection}/${id}/retirer`,
        { code: code.trim(), motif: motif.trim() });
      if (data && data.resultat === 'retire') {
        toast.success('Cadenas retiré. Le document redevient modifiable.');
        window.dispatchEvent(new CustomEvent(EVT_DEVERROUILLE, { detail: { collection, id } }));
        fermer();
      } else {
        setErreur(`Cadenas non retiré : ${(data && data.resultat) || '?'}${data && data.raison ? ` — ${data.raison}` : ''}`);
      }
    } catch (err) {
      const d = err && err.response && err.response.data && err.response.data.detail;
      setErreur(typeof d === 'string' ? d : (d && d.message) || 'Déverrouillage refusé.');
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cadenas-titre"
      data-testid="cadenas-popup"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) fermer(); }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-[#F5B544]/30 bg-[#141416] shadow-2xl">
        <div className="flex items-start gap-3 border-b border-white/10 px-5 py-4">
          <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#F59E0B]/15 text-[#F5B544]">
            <Lock size={18} weight="fill" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="cadenas-titre" className="text-[15px] font-semibold text-white">Fichier verrouillé</h2>
            <p className="mt-0.5 truncate text-[12px] text-[#A1A1AA]">{cible.titre || 'Document cadenassé'}</p>
          </div>
          <button type="button" onClick={fermer} aria-label="Fermer"
            className="rounded-lg p-1.5 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <p className="text-[13px] leading-relaxed text-[#D4D4D8]">
            Ce document porte le cadenas fondateur. Il reste consultable et téléchargeable.
            Aucune modification, aucun déplacement et aucune suppression ne sont possibles
            tant que le cadenas n'est pas retiré.
          </p>

          {(cible.pose_le || cible.pose_par || cible.motif) && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[12px] text-[#A1A1AA]">
              {cible.pose_le && <div>Posé le <span className="text-[#D4D4D8]">{frDate(cible.pose_le)}</span>
                {cible.pose_par ? <> par <span className="text-[#D4D4D8]">{cible.pose_par}</span></> : null}</div>}
              {cible.motif && <div className="mt-1 italic">{cible.motif}</div>}
            </div>
          )}

          {!politique && (
            <div className="flex items-center gap-2 text-[12px] text-[#A1A1AA]">
              <SpinnerGap size={14} className="animate-spin" /> Vérification de vos droits…
            </div>
          )}

          {politique && !peutSaisir && (
            <div className="rounded-xl border border-[#F5B544]/25 bg-[#F59E0B]/[0.07] px-3 py-3 text-[13px] text-[#D4D4D8]">
              Pour déverrouiller ce document, adressez-vous à{' '}
              <span className="font-semibold text-[#F5B544]">{contacts.join(' ou ')}</span>.
            </div>
          )}

          {politique && peutSaisir && !politique.defini && (
            <div className="flex items-start gap-2 rounded-xl border border-[#F5B544]/25 bg-[#F59E0B]/[0.07] px-3 py-3 text-[13px] text-[#D4D4D8]">
              <Warning size={16} className="mt-0.5 flex-shrink-0 text-[#F5B544]" />
              <span>Aucun code n'a encore été créé. Créez-le dans
                <span className="font-semibold text-white"> Paramètres → Cadenas fondateur</span>.</span>
            </div>
          )}

          {politique && peutSaisir && politique.defini && (
            <form onSubmit={deverrouiller} className="space-y-3">
              <div>
                <label htmlFor="cadenas-code" className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#A1A1AA]">
                  Code du cadenas
                </label>
                <input
                  id="cadenas-code" type="password" autoFocus autoComplete="off"
                  data-testid="cadenas-code"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Le code que vous avez défini"
                  className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-[13px] text-white outline-none transition-colors placeholder:text-[#52525B] focus:border-[#F5B544]/50"
                />
                {politique.indice ? (
                  <p className="mt-1 text-[11px] text-[#71717A]">Aide-mémoire : {politique.indice}</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="cadenas-motif" className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#A1A1AA]">
                  Motif (tracé au journal)
                </label>
                <input
                  id="cadenas-motif" type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
                  data-testid="cadenas-motif"
                  placeholder="Pourquoi ce document doit-il changer ?"
                  className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-[13px] text-white outline-none transition-colors placeholder:text-[#52525B] focus:border-[#F5B544]/50"
                />
              </div>
              {erreur ? <p data-testid="cadenas-erreur" className="text-[12px] text-[#F87171]">{erreur}</p> : null}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button" onClick={fermer}
                  className="rounded-xl border border-white/12 px-3 py-2 text-[12px] font-medium text-[#A1A1AA] transition-colors hover:bg-white/[0.06] hover:text-white">
                  Annuler
                </button>
                <button type="submit" disabled={busy || !deverrouillable}
                  data-testid="cadenas-deverrouiller"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#F5B544] px-3.5 py-2 text-[12px] font-semibold text-[#141416] transition-opacity hover:opacity-90 disabled:opacity-50">
                  {busy ? <SpinnerGap size={14} className="animate-spin" /> : <LockOpen size={14} weight="bold" />}
                  Déverrouiller
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
